// @flow
import _ from 'lodash'
import AdditionalVkImageUploader from './AdEdit/AdditionalVkImageUploader'
import AdEditReplicaContainer from './AdEdit/AdEditReplicaContainer'
import AdInputs from './AdEdit/AdInputs'
import ImageSizeRestrictionsMerger from './AdEdit/ImageSizeRestrictionsMerger'
import LinkButtonsContainer from './AdEdit/AdInputs/LinkButtonsContainer'
import PrimaryButton from './controls/PrimaryButton'
import React, { Component, Fragment } from 'react'
import ReplicatedData from './AdEdit/ReplicatedData'
import styled, { type StyledComponent } from 'styled-components'
import type { AdvertisementReplica } from '../redux/ducks/advertisementReplicas/reducers'
import type {
  AudienceData,
  Configs,
  ControlsInfo,
  Crop,
  ImageConfig,
  ImageSizeRestrictions,
  OptionType,
  ProviderImageConfigs,
  UrlCheckResult
} from './types'
import { editPath as advertisementTemplateEditPath } from './AdvertisementTemplatesTable'
import { translations, translateFunc } from '../shared/localize'
import CarouselContainer from './AdEdit/CarouselContainer'

const maxWidth = 675

const Wrapper: StyledComponent<Object, any, any> = styled.div`
  line-height: 100%;
  font-family: 'PT Sans';
`
const FormError = styled.div`
  clear: both;
  margin: 20px 0;
  color: red;
  font-weight: bold;
`
export const PaddedWrapper: StyledComponent<Object, any, any> = styled.div`
  padding-top: 10px;
`
export const Media: StyledComponent<Object, any, any> = styled.div`
  display: flex;
  margin-right: 5px;
`
export const MediaSpacer: StyledComponent<Object, any, any> = styled.div`
  width: 5px;
`
export const CropWrapper: StyledComponent<Object, any, any> = styled.div`
  padding-right: 4px;
`
const AdvertisementTemplateWarning: StyledComponent<Object, any, any> = styled.div`
  border: solid 1px red;
  padding: 8px 16px;
  margin: 10px 0;
  max-width: ${maxWidth}px;
  line-height: 125%;
`
const ReplicaWrapper = styled.div`
  padding: 10px 0 5px 0;
`
export const Row: StyledComponent<Object, any, any> = styled.div`
  padding-bottom: 14px;
  max-width: 675px;
`

export function getImageSizeRestrictionsFromSingleConfig(
  config: ImageConfig
): ImageSizeRestrictions {
  const { minWidth, minHeight, maxWidth, maxHeight } = config

  return { minWidth, minHeight, maxWidth, maxHeight }
}

export function getImageSizeRestrictionsFromConfigs(
  configs: ProviderImageConfigs,
  providers: Array<string>
): ImageSizeRestrictions {
  return new ImageSizeRestrictionsMerger(_.values(_.pick(configs, providers))).restrictions()
}

export type ControlVisibility = ControlsInfo<boolean>

type Errors = Array<string>

export type Props = {|
  action: 'copy' | 'update' | 'create',
  advertisementTemplateId: ?number,
  audienceData: AudienceData, // eslint-disable-line react/no-unused-prop-types
  carouselStoreId: ?string,
  columnDescription: string,
  columnDescriptionErrors: Errors,
  columnImageErrors: Errors,
  columnImageSizeRestrictions: ImageSizeRestrictions,
  columnImageStoreId: ?string,
  compact: boolean, // eslint-disable-line react/no-unused-prop-types
  compactUrl: boolean,
  configs: Configs,
  controlVisibility: ControlVisibility,
  fbImageCrop: ?Crop,
  fbInstagramImageCrop: ?Crop,
  fbInstagramImageErrors: Errors,
  fbInstagramImageStoreId: ?string,
  fbLinkButton: string,
  fbMultiDescription: string,
  fbMultiDescriptionErrors: Errors,
  fbMultiImageConfig: ImageConfig,
  fbPromotionPosts: Array<OptionType>,
  fbStoriesImageCrop: ?Crop,
  fbStoriesImageErrors: Errors,
  fbStoriesImageStoreId: ?string,
  fbThumbnailImageCrop: ?Crop,
  fbThumbnailImageErrors: Errors,
  fbThumbnailImageStoreId: ?string,
  fbTitle: string,
  fbTitleErrors: Errors,
  fbUrl: string,
  fbUrlErrors: Errors,
  glImageCrop: ?Crop,
  glMultiImageConfig: ImageConfig,
  hasAnyErrors: boolean,
  hasAnyMedias: boolean,
  hasVideo: boolean,
  imageConfigs: ProviderImageConfigs,
  isPromotionUrlEditDisabled: boolean,
  logoImageCrop: ?Crop,
  logoImageErrors: Errors,
  logoImageStoreId: ?string,
  maxMediaCount: number,
  multiDescription: string,
  multiDescriptionErrors: Errors,
  multiImageErrors: Errors,
  multiImageSizeRestrictions: ImageSizeRestrictions,
  multiImageStoreId: ?string,
  onChange: (diff: Object) => void, // eslint-disable-line react/no-unused-prop-types
  onColumnDescriptionChange: (string) => void,
  onColumnImageChange: (Array<string>) => void,
  onFbImageCropChange: (Crop) => void,
  onFbInstagramImageChange: (string) => void,
  onFbInstagramImageCropChange: (Crop) => void,
  onFbLinkButtonChange: (string) => void,
  onFbMultiDescriptionChange: (string) => void,
  onFbStoriesImageChange: (string) => void,
  onFbStoriesImageCropChange: (Crop) => void,
  onFbThumbnailImageChange: (string) => void,
  onFbThumbnailImageCropChange: (Crop) => void,
  onFbTitleChange: (string) => void,
  onFbUrlChange: (string) => void,
  onGlImageCropChange: (Crop) => void,
  onImageStoreIdChange: (Array<string>) => void,
  onLogoImageChange: (string) => void,
  onLogoImageCropChange: (?Crop) => void,
  onMediaRemove: () => void,
  onMultiDescriptionChange: (string) => void,
  onProductDescriptionChange: (string) => void,
  onProductTitleChange: (string) => void,
  onReplicaAdd: () => void,
  onSnippetImageChange: (string, Array<AdvertisementReplica>) => void,
  onSnippetImageCropChange: (Crop, Array<AdvertisementReplica>) => void,
  onTitleChange: (string) => void,
  onTmCarouselDescriptionChange: (string) => void,
  onTmColumnCropChange: (Crop) => void,
  onTmLinkButtonChange: (string) => void,
  onTmMultiCropChange: (Crop) => void,
  onTmSmallImageChange: (string) => void,
  onTmSmallImageCropChange: (Crop) => void,
  onTmUrlChange: (string) => void,
  onUrlChange: (string) => void,
  onVideoChange: (string) => void,
  onVkArticleCropChange: (Crop) => void,
  onVkBigImageChange: (Array<string>) => void,
  onVkColumnCropChange: (Crop) => void,
  onVkLinkButtonChange: (string) => void,
  onVkLinkTitleChange: (string) => void,
  onVkMultiCropChange: (Crop) => void,
  onVkMultiDescriptionChange: (string) => void,
  onVkSiteCropChange: (Crop) => void,
  onVkTitleChange: (string) => void,
  onVkUrlChange: (string) => void,
  onVkWithoutButtonDescriptionChange: (string) => void,
  productDescription: string,
  productDescriptionErrors: Errors,
  productTitle: string,
  productTitleErrors: Errors,
  replicas: Array<AdvertisementReplica>,
  snippetImageCrop: ?Crop,
  snippetImageErrors: Errors,
  snippetImageStoreId: ?string,
  storeId: string, // eslint-disable-line react/no-unused-prop-types
  title: string,
  titleErrors: Errors,
  tmCarouselDescription: string,
  tmCarouselDescriptionErrors: Errors,
  tmColumnCrop: ?Crop,
  tmLinkButton: string,
  tmMultiCrop: ?Crop,
  tmMultiImageConfig: ImageConfig,
  tmSmallImageCrop: ?Crop,
  tmSmallImageErrors: Errors,
  tmSmallImageStoreId: ?string,
  tmUrl: string,
  tmUrlErrors: Array<string>,
  url: string,
  urlCheck: ?UrlCheckResult,
  urlErrors: Errors,
  videoErrors: Errors,
  videoStoreId: ?string,
  vkArticleCrop: ?Crop,
  vkBigImageErrors: Errors,
  vkBigImageSizeRestrictions: ImageSizeRestrictions,
  vkBigImageStoreId: ?string,
  vkColumnCrop: ?Crop,
  vkLinkButton: string,
  vkLinkTitle: string,
  vkLinkTitleErrors: Errors,
  vkMultiCrop: ?Crop,
  vkMultiDescription: string,
  vkMultiDescriptionErrors: Errors,
  vkMultiImageConfig: ImageConfig,
  vkOptionsWithoutPublic: boolean,
  vkSiteCrop: ?Crop,
  vkTitle: string,
  vkTitleErrors: Errors,
  vkUrl: string,
  vkUrlErrors: Errors,
  vkWithoutButtonDescription: string,
  vkWithoutButtonDescriptionErrors: Errors
|}

const tFunc = translateFunc(translations.ad_edit)

const t = {
  choose_logo_image: 'загрузить свой логотип',
  choose_snippet_image: 'загрузить сниппет'
}

export default class AdEdit extends Component<Props> {
  render() {
    const visibility = this.props.controlVisibility
    return (
      <Wrapper className="t-AdEdit">
        {this.renderAdvertisementTemplateWarning(this.props.advertisementTemplateId)}
        {this.props.hasAnyErrors && <FormError>{tFunc('form_has_errors')}</FormError>}
        <AdInputs
          allowCutView
          columnDescription={this.props.columnDescription}
          columnDescriptionErrors={this.props.columnDescriptionErrors}
          configs={this.props.configs}
          fbMultiDescription={this.props.fbMultiDescription}
          fbMultiDescriptionErrors={this.props.fbMultiDescriptionErrors}
          fbPromotionPosts={this.props.fbPromotionPosts}
          fbTitle={this.props.fbTitle}
          fbTitleErrors={this.props.fbTitleErrors}
          fbUrl={this.props.fbUrl}
          fbUrlErrors={this.props.fbUrlErrors}
          isFbUrlDisabled={this.props.isPromotionUrlEditDisabled}
          isTmUrlDisabled={this.props.isPromotionUrlEditDisabled}
          isVkUrlDisabled={this.props.isPromotionUrlEditDisabled}
          multiDescription={this.props.multiDescription}
          multiDescriptionErrors={this.props.multiDescriptionErrors}
          onColumnDescriptionChange={this.props.onColumnDescriptionChange}
          onFbMultiDescriptionChange={this.props.onFbMultiDescriptionChange}
          onFbTitleChange={(val) => this.props.onFbTitleChange(val)}
          onFbUrlChange={this.props.onFbUrlChange}
          onMultiDescriptionChange={this.props.onMultiDescriptionChange}
          onProductDescriptionChange={this.props.onProductDescriptionChange}
          onProductTitleChange={this.props.onProductTitleChange}
          onTitleChange={this.props.onTitleChange}
          onTmCarouselDescriptionChange={this.props.onTmCarouselDescriptionChange}
          onTmUrlChange={this.props.onTmUrlChange}
          onUrlChange={this.props.onUrlChange}
          onVkLinkTitleChange={this.props.onVkLinkTitleChange}
          onVkMultiDescriptionChange={this.props.onVkMultiDescriptionChange}
          onVkTitleChange={(val) => this.props.onVkTitleChange(val)}
          onVkUrlChange={this.props.onVkUrlChange}
          onVkWithoutButtonDescriptionChange={this.props.onVkWithoutButtonDescriptionChange}
          productDescription={this.props.productDescription}
          productDescriptionErrors={this.props.productDescriptionErrors}
          productTitle={this.props.productTitle}
          productTitleErrors={this.props.productTitleErrors}
          showColumnDescription={visibility.columnDescription}
          showFbMultiDescription={visibility.fbMultiDescription}
          showFbTitle={visibility.fbTitle}
          showFbUrl={visibility.fbUrl}
          showMultiDescription={visibility.multiDescription}
          showProductDescription={visibility.productDescription}
          showProductTitle={visibility.productTitle}
          showTitle={visibility.title}
          showTmCarouselDescription={visibility.tmCarouselDescription}
          showTmUrl={visibility.tmUrl}
          showUrl={visibility.url}
          showVkLinkTitle={visibility.vkLinkTitle}
          showVkMultiDescription={visibility.vkMultiDescription}
          showVkTitle={visibility.vkTitle}
          showVkUrl={visibility.vkUrl}
          showVkWithoutButtonDescription={visibility.vkWithoutButtonDescription}
          title={this.props.title}
          titleErrors={this.props.titleErrors}
          tmCarouselDescription={this.props.tmCarouselDescription}
          tmCarouselDescriptionErrors={this.props.tmCarouselDescriptionErrors}
          tmUrl={this.props.tmUrl}
          tmUrlErrors={this.props.tmUrlErrors}
          url={this.props.url}
          urlCompact={this.props.compactUrl}
          urlErrors={this.props.urlErrors}
          vkLinkTitle={this.props.vkLinkTitle}
          vkLinkTitleErrors={this.props.vkLinkTitleErrors}
          vkMultiDescription={this.props.vkMultiDescription}
          vkMultiDescriptionErrors={this.props.vkMultiDescriptionErrors}
          vkTitle={this.props.vkTitle}
          vkTitleErrors={this.props.vkTitleErrors}
          vkUrl={this.props.vkUrl}
          vkUrlErrors={this.props.vkUrlErrors}
          vkWithoutButtonDescription={this.props.vkWithoutButtonDescription}
          vkWithoutButtonDescriptionErrors={this.props.vkWithoutButtonDescriptionErrors}
        />
        {visibility.carousel && this.props.carouselStoreId && (
          <CarouselContainer
            storeId={this.props.carouselStoreId}
            configs={this.props.configs}
            controlVisibility={this.props.controlVisibility}
          />
        )}
        <LinkButtonsContainer
          url={this.props.url}
          urlCheck={this.props.urlCheck}
          configs={this.props.configs}
          vkValue={this.props.vkLinkButton}
          tmValue={this.props.tmLinkButton}
          fbValue={this.props.fbLinkButton}
          onVkChange={this.props.onVkLinkButtonChange}
          onTmChange={this.props.onTmLinkButtonChange}
          onFbChange={this.props.onFbLinkButtonChange}
          hideVk={!visibility.vkLinkButton}
          hideTm={!visibility.tmLinkButton}
          hideFb={!visibility.fbLinkButton}
          vkErrors={[]}
          tmErrors={[]}
          fbErrors={[]}
          vkOptionsWithoutPublic={this.props.vkOptionsWithoutPublic}
        />
        {this.props.hasAnyMedias && (
          <Fragment>
            {this.renderReplicatedData()}
            {!this.props.hasVideo && (
              <Fragment>
                {this.renderLogoUploader()}
                {this.renderSnippetUploader()}
              </Fragment>
            )}
          </Fragment>
        )}
      </Wrapper>
    )
  }

  renderAdvertisementTemplateWarning(templateId: ?number) {
    return (
      templateId && (
        <AdvertisementTemplateWarning>
          {tFunc('based_on_template')}.{' '}
          <a href={advertisementTemplateEditPath(templateId)}>{tFunc('edit_template')}</a>
        </AdvertisementTemplateWarning>
      )
    )
  }

  renderLogoUploader() {
    const { configs, logoImageStoreId, logoImageCrop, logoImageErrors } = this.props

    return (
      <div className="t-LogoImage">
        <AdditionalVkImageUploader
          showImage={this.props.controlVisibility.logoImage}
          imageStoreId={logoImageStoreId}
          loaderText={tFunc('logo_loader_text')}
          loaderTitle={t.choose_logo_image}
          cropperTitle={tFunc('logo_header')}
          crop={logoImageCrop}
          imageConfigs={configs.logoImageConfigs}
          errors={logoImageErrors}
          onLoad={(storeId) => this.props.onLogoImageChange(storeId)}
          onChange={(crop) => this.props.onLogoImageCropChange(crop)}
        />
      </div>
    )
  }

  renderSnippetUploader() {
    const { snippetImageStoreId, snippetImageCrop, snippetImageErrors } = this.props

    if (!this.props.controlVisibility.snippetImage) return null

    return (
      <div className="t-SnippetImage">
        <AdditionalVkImageUploader
          showImage={this.props.controlVisibility.snippetImage}
          imageStoreId={snippetImageStoreId}
          loaderText={tFunc('snippet_loader_text')}
          loaderTitle={t.choose_snippet_image}
          cropperTitle={tFunc('snippet_header')}
          crop={snippetImageCrop}
          imageConfigs={this.props.imageConfigs.vk}
          errors={snippetImageErrors}
          onLoad={(storeId) => {
            this.props.onSnippetImageChange(storeId, this.props.replicas)
          }}
          onChange={(crop) => {
            this.props.onSnippetImageCropChange(crop, this.props.replicas)
          }}
        />
      </div>
    )
  }

  renderReplicatedData() {
    return (
      <Fragment>
        <ReplicatedData
          action={this.props.action}
          allowMultiUpload
          columnImageErrors={this.props.columnImageErrors}
          columnImageSizeRestrictions={this.props.columnImageSizeRestrictions}
          columnImageStoreId={this.props.columnImageStoreId}
          configs={this.props.configs}
          controlVisibility={this.props.controlVisibility}
          fbImageCrop={this.props.fbImageCrop}
          fbInstagramImageCrop={this.props.fbInstagramImageCrop}
          fbInstagramImageErrors={this.props.fbInstagramImageErrors}
          fbInstagramImageStoreId={this.props.fbInstagramImageStoreId}
          fbMultiImageConfig={this.props.fbMultiImageConfig}
          fbStoriesImageCrop={this.props.fbStoriesImageCrop}
          fbStoriesImageErrors={this.props.fbStoriesImageErrors}
          fbStoriesImageStoreId={this.props.fbStoriesImageStoreId}
          fbThumbnailImageCrop={this.props.fbThumbnailImageCrop}
          fbThumbnailImageErrors={this.props.fbThumbnailImageErrors}
          fbThumbnailImageStoreId={this.props.fbThumbnailImageStoreId}
          glImageCrop={this.props.glImageCrop}
          glMultiImageConfig={this.props.glMultiImageConfig}
          isReplica={false}
          multiImageErrors={this.props.multiImageErrors}
          multiImageSizeRestrictions={this.props.multiImageSizeRestrictions}
          multiImageStoreId={this.props.multiImageStoreId}
          onColumnImageChange={this.props.onColumnImageChange}
          onFbImageCropChange={this.props.onFbImageCropChange}
          onFbInstagramImageChange={this.props.onFbInstagramImageChange}
          onFbInstagramImageCropChange={this.props.onFbInstagramImageCropChange}
          onFbStoriesImageChange={this.props.onFbStoriesImageChange}
          onFbStoriesImageCropChange={this.props.onFbStoriesImageCropChange}
          onFbThumbnailImageChange={this.props.onFbThumbnailImageChange}
          onFbThumbnailImageCropChange={this.props.onFbThumbnailImageCropChange}
          onGlImageCropChange={this.props.onGlImageCropChange}
          onMediaRemove={this.props.onMediaRemove}
          onMultiImageChange={this.props.onImageStoreIdChange}
          onTmColumnImageCropChange={this.props.onTmColumnCropChange}
          onTmMultiImageCropChange={this.props.onTmMultiCropChange}
          onTmSmallImageChange={this.props.onTmSmallImageChange}
          onTmSmallImageCropChange={this.props.onTmSmallImageCropChange}
          onVideoChange={this.props.onVideoChange}
          onVkArticleImageCropChange={this.props.onVkArticleCropChange}
          onVkBigImageChange={this.props.onVkBigImageChange}
          onVkColumnImageCropChange={this.props.onVkColumnCropChange}
          onVkMultiImageCropChange={this.props.onVkMultiCropChange}
          onVkSiteImageCropChange={this.props.onVkSiteCropChange}
          replica={null} // unused
          replicaIndex={0} // unused
          tmColumnImageCrop={this.props.tmColumnCrop}
          tmMultiImageConfig={this.props.tmMultiImageConfig}
          tmMultiImageCrop={this.props.tmMultiCrop}
          tmSmallImageCrop={this.props.tmSmallImageCrop}
          tmSmallImageErrors={this.props.tmSmallImageErrors}
          tmSmallImageStoreId={this.props.tmSmallImageStoreId || this.props.multiImageStoreId}
          videoErrors={this.props.videoErrors}
          videoStoreId={this.props.videoStoreId}
          vkArticleImageCrop={this.props.vkArticleCrop}
          vkBigImageErrors={this.props.vkBigImageErrors}
          vkBigImageSizeRestrictions={this.props.vkBigImageSizeRestrictions}
          vkBigImageStoreId={this.props.vkBigImageStoreId}
          vkColumnImageCrop={this.props.vkColumnCrop}
          vkMultiImageConfig={this.props.vkMultiImageConfig}
          vkMultiImageCrop={this.props.vkMultiCrop}
          vkSiteImageCrop={this.props.vkSiteCrop}
        />
        {this.renderReplicas()}
      </Fragment>
    )
  }

  renderReplicas() {
    const { maxMediaCount, replicas } = this.props

    return (
      <Fragment>
        {_.map(replicas, (replica, index: number) => {
          return (
            <ReplicaWrapper key={index}>
              <AdEditReplicaContainer
                action={this.props.action}
                controlVisibility={this.props.controlVisibility}
                replica={replica}
                replicaIndex={index}
                configs={this.props.configs}
                columnImageSizeRestrictions={this.props.columnImageSizeRestrictions}
                multiImageSizeRestrictions={this.props.multiImageSizeRestrictions}
                vkBigImageSizeRestrictions={this.props.vkBigImageSizeRestrictions}
                vkMultiImageConfig={this.props.vkMultiImageConfig}
                tmMultiImageConfig={this.props.tmMultiImageConfig}
                fbMultiImageConfig={this.props.fbMultiImageConfig}
                glMultiImageConfig={this.props.glMultiImageConfig}
              />
            </ReplicaWrapper>
          )
        })}{' '}
        {maxMediaCount > replicas.length + 1 && (
          <PaddedWrapper>
            <PrimaryButton height={32} onClick={() => this.props.onReplicaAdd()}>
              {tFunc('add_replica')}
            </PrimaryButton>
          </PaddedWrapper>
        )}
      </Fragment>
    )
  }
}
